import { FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth'

import { GoogleButton } from '../components/google-button'
import { NaydaStacked } from '../components/nayda-mark'
import { firebaseApp } from '../firebase'

export function Signup() {
	const navigate = useNavigate()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)

	async function handleSubmit(e: FormEvent) {
		e.preventDefault()
		setError(null)
		setLoading(true)
		try {
			const auth = getAuth(firebaseApp)
			await createUserWithEmailAndPassword(auth, email, password)
			// Account is auto-created on first API call (AccountService.Me).
			navigate('/')
		} catch (err) {
			setError(humanizeFirebaseError(err))
		} finally {
			setLoading(false)
		}
	}

	return (
		<div className="min-h-screen bg-bone flex items-center justify-center p-6">
			<div className="w-full max-w-[400px]">
				<div className="mb-10 flex justify-center">
					<NaydaStacked markSize={80} />
				</div>

				<div className="card p-8">
					<div className="mb-6">
						<h1 className="font-sans font-medium text-[26px] leading-tight text-ink">
							Cross every border.
						</h1>
						<p className="text-ink-soft text-[14px] mt-1">
							Point us at your site. We'll sniff out the strings.
						</p>
					</div>

					<GoogleButton onError={setError} />

					<div className="flex items-center gap-3 my-5">
						<div className="h-px flex-1 bg-line" />
						<span className="label-mono">or</span>
						<div className="h-px flex-1 bg-line" />
					</div>

					<form
						onSubmit={(e) => void handleSubmit(e)}
						className="flex flex-col gap-4"
					>
						<label className="flex flex-col gap-1.5">
							<span className="field-label">Email</span>
							<input
								type="email"
								required
								autoFocus
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="field"
								placeholder="you@company.com"
							/>
						</label>

						<label className="flex flex-col gap-1.5">
							<span className="field-label">Password</span>
							<div className="relative">
								<input
									type={showPassword ? 'text' : 'password'}
									required
									minLength={6}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="field pr-11"
									placeholder="At least 6 characters"
								/>
								<button
									type="button"
									onClick={() => setShowPassword((v) => !v)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
									aria-pressed={showPassword}
									className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-nayda-sm text-ink-soft hover:text-ink hover:bg-bone focus:outline-none focus-visible:ring-2 focus-visible:ring-marigold"
								>
									<EyeIcon shown={showPassword} />
								</button>
							</div>
						</label>

						{error && (
							<div className="text-[13px] text-[#B23B2A] bg-[#B23B2A]/10 rounded-nayda-sm px-3 py-2 border border-[#B23B2A]/20">
								{error}
							</div>
						)}

						<button
							type="submit"
							disabled={loading}
							className="btn mt-2"
						>
							{loading ? 'Creating account…' : 'Create account'}
						</button>

						<p className="text-[12px] text-ink-mute text-center mt-1">
							Free plan: 1 language, 100 000 characters / month. No card required.
						</p>
					</form>
				</div>

				<div className="mt-6 text-center text-[13px] text-ink-soft">
					Already have an account?{' '}
					<Link
						to="/login"
						className="text-ink underline underline-offset-2 hover:text-ember"
					>
						Sign in
					</Link>
				</div>
			</div>
		</div>
	)
}

/** Same eye toggle as on the login page — keeps the visual language consistent. */
function EyeIcon({ shown }: { shown: boolean }) {
	if (shown) {
		return (
			<svg
				width="18"
				height="18"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden
			>
				<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
				<circle
					cx="12"
					cy="12"
					r="3"
				/>
			</svg>
		)
	}
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
			<line
				x1="1"
				y1="1"
				x2="23"
				y2="23"
			/>
		</svg>
	)
}

function humanizeFirebaseError(err: unknown): string {
	const code = (err as { code?: string })?.code
	if (code === 'auth/email-already-in-use') {
		return 'Email is already registered. Try signing in.'
	}
	if (code === 'auth/weak-password') {
		return 'Password is too weak (min 6 characters).'
	}
	if (code === 'auth/invalid-email') {
		return 'Invalid email address.'
	}
	return err instanceof Error ? err.message : 'Sign-up failed'
}
